import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "context/LanguageContext";
import { useNotification } from "context/NotificationContext";
import {
  renderYesNoField,
  renderRangeField,
  renderInputTextField,
  renderCheckboxGroup,
  convertYesNoFieldsToString,
  validateFields,
  handleConfirmSave,
  handleNavigateBack
} from "components/formUtils";

const PosOperatorioForm = () => {
  const { GetTranslation } = useLanguage();
  const notify = useNotification();
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    tempo_permanencia_uti_horas: "",
    tempo_permanencia_internacao_dias: "",
    tempo_extubacao_apos_admissao_uti_horas: "",
    extubacao_em_sala: "",
    dor_pos_operatoria: "",
    escala_visual_analogica_dor: "",
    tempo_inicio_mobilizacao_leito_horas: "",
    tempo_inicio_deambulacao_horas: "",
    infeccao: [],
    deiscencia_ferida_operatoria: [],
    infarto_miocardio_pos_operatorio: "",
    nova_revascularizacao_nao_programada: "",
    primeira_dialise: "",
    ave_novo_com_sequela: "",
    fibrilacao_atrial_pos_operatoria: "",
    parada_cardiorrespiratoria_revertida: "",
    necessidade_dispositivo_assistencia_circulatoria: "",
    tipo_dispositivo_assistencia_circulatoria: "",
    tempo_inicio_retirada_drenos_horas: "",
  });

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (id) {
      fetch(`${process.env.REACT_APP_API_URL}/pos_operatorio/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            const yesNoFields = [
              "extubacao_em_sala",
              "dor_pos_operatoria",
              "infarto_miocardio_pos_operatorio",
              "nova_revascularizacao_nao_programada",
              "primeira_dialise",
              "ave_novo_com_sequela",
              "fibrilacao_atrial_pos_operatoria",
              "parada_cardiorrespiratoria_revertida",
              "necessidade_dispositivo_assistencia_circulatoria",
            ];
            const convertedData = convertYesNoFieldsToString(data.data, yesNoFields);
            setFormData(convertedData);
          } else {
            notify({
              type: "error",
              message: data.error || "Erro ao buscar os dados",
            });
          }
        })
        .catch(() => {
          notify({
            type: "error",
            message: "Erro ao buscar os dados",
          });
        });
    }
  }, [id, token, notify]);

  const requiredFields = [];

  const apiUrl = id
    ? `${process.env.REACT_APP_API_URL}/pos_operatorio/update/${id}`
    : `${process.env.REACT_APP_API_URL}/pos_operatorio/add`;

  const handleSubmit = () => {
    const method = id ? "PUT" : "POST";
    fetch(apiUrl, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        if (response.ok) {
          notify({
            type: "success",
            message: id ? "Pós-Operatório atualizado com sucesso!" : "Pós-Operatório adicionado com sucesso!",
          });
        } else {
          response.json().then((data) => {
            notify({
              type: "error",
              message: `Erro: ${data.error}`,
            });
          });
        }
      })
      .catch(() => {
        notify({
          type: "error",
          message: "Erro ao enviar os dados",
        });
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Função para lidar com checkbox
  const handleCheckboxChange = (e, field) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const updatedValues = checked
        ? [...prev[field], value] // Adiciona se estiver checado
        : prev[field].filter((v) => v !== value); // Remove se desmarcado
      return { ...prev, [field]: updatedValues };
    });
  };

  const confirmSave = handleConfirmSave(formData, requiredFields, validateFields, GetTranslation, notify, handleSubmit);
  const handleBack = handleNavigateBack(navigate, id);

  return (
    <div className="card p-3">
      <ConfirmDialog />
      <h4>{id ? GetTranslation("Edit_PosOperatorio") : GetTranslation("Add_PosOperatorio")}</h4>
      <form className="row g-3">
        {/* Tempo de Permanência em UTI */}
        {renderRangeField(
          formData,
          setFormData,
          "tempo_permanencia_uti_horas",
          "Tempo de permanência em Unidade de Terapia Intensiva",
          0,
          1000,
          0,
          GetTranslation,
          "(horas)"
        )}

        {/* Tempo de Permanência Hospitalar */}
        {renderRangeField(
          formData,
          setFormData,
          "tempo_permanencia_internacao_dias",
          "Tempo de permanência em internação Hospitalar",
          0,
          1000,
          0,
          GetTranslation,
          "(dias)"
        )}

        {/* Extubação em Sala */}
        {renderYesNoField(
          formData,
          handleChange,
          "extubacao_em_sala",
          "Exturbação em sala",
          GetTranslation,
        )}

        {/* Tempo de Extubação após Admissão na UTI */}
        {formData.extubacao_em_sala === "0" &&
          renderRangeField(
            formData,
            setFormData,
            "tempo_extubacao_apos_admissao_uti_horas",
            "Tempo de extubação após admissão na UTI",
            0,
            1000,
            0,
            GetTranslation,
            "(horas)"
          )}

        {/* Dor Pós-Operatória */}
        {renderYesNoField(
          formData,
          handleChange,
          "dor_pos_operatoria",
          "Dor pós-operatória",
          GetTranslation,
          true // Campo obrigatório
        )}

        {/* Escala Visual Analógica da Dor */}
        {formData.dor_pos_operatoria === "1" &&
          renderRangeField(
            formData,
            setFormData,
            "escala_visual_analogica_dor",
            "Escala visual analógica da dor (0-10)",
            0,
            10,
            0,
            GetTranslation
          )}

        {/* Tempo para Início de Mobilização no Leito */}
        {renderRangeField(
          formData,
          setFormData,
          "tempo_inicio_mobilizacao_leito_horas",
          "Tempo para início de mobilização no leito",
          0,
          100,
          0,
          GetTranslation,
          "(horas)"
        )}

        {/* Tempo para Início de Deambulação */}
        {renderRangeField(
          formData,
          setFormData,
          "tempo_inicio_deambulacao_horas",
          "Tempo para início de deambulação",
          0,
          100,
          0,
          GetTranslation,
          "(horas)"
        )}

        {/* Infecção */}
        {renderCheckboxGroup(
          formData,
          handleCheckboxChange,
          "infeccao",
          "Infecção",
          ["Não", "Ferida operatória", "Outro sítio"],
          GetTranslation
        )}

        {/* Deiscência de Ferida Operatória */}
        {renderCheckboxGroup(
          formData,
          handleCheckboxChange,
          "deiscencia_ferida_operatoria",
          "Deiscência de ferida operatória",
          ["Não", "Esternal", "Inguinal", "Femoral", "Intercostal", "Periareolar"],
          GetTranslation
        )}

        {/* Infarto do Miocárdio Pós-Operatório */}
        {renderYesNoField(
          formData,
          handleChange,
          "infarto_miocardio_pos_operatorio",
          "Infarto do miocárdio pós-operatório",
          GetTranslation
        )}

        {/* Nova Revascularização não Programada */}
        {formData.infarto_miocardio_pos_operatorio === "1" &&
          renderYesNoField(
            formData,
            handleChange,
            "nova_revascularizacao_nao_programada",
            "Necessidade de nova revascularização não programada",
            GetTranslation
          )}

        {/* Necessidade de Primeira Diálise */}
        {renderYesNoField(
          formData,
          handleChange,
          "primeira_dialise",
          "Necessidade de primeira diálise",
          GetTranslation
        )}

        {/* AVE Novo com Sequela */}
        {renderYesNoField(
          formData,
          handleChange,
          "ave_novo_com_sequela",
          "AVE novo com sequela",
          GetTranslation
        )}

        {/* Fibrilação Atrial Pós-Operatória */}
        {renderYesNoField(
          formData,
          handleChange,
          "fibrilacao_atrial_pos_operatoria",
          "Fibrilação Atrial pós-operatória",
          GetTranslation
        )}

        {/* Parada Cardiorrespiratória Revertida */}
        {renderYesNoField(
          formData,
          handleChange,
          "parada_cardiorrespiratoria_revertida",
          "Parada cardiorrespiratória revertida no pós-operatório",
          GetTranslation
        )}

        {/* Necessidade de Dispositivo de Assistência Circulatória */}
        {renderYesNoField(
          formData,
          handleChange,
          "necessidade_dispositivo_assistencia_circulatoria",
          "Necessidade de Dispositivo de Assistência Circulatória",
          GetTranslation
        )}

        {/* Tipo de Dispositivo de Assistência Circulatória */}
        {formData.necessidade_dispositivo_assistencia_circulatoria === "1" &&
          renderInputTextField(
            formData,
            handleChange,
            "tipo_dispositivo_assistencia_circulatoria",
            "Tipo de dispositivo de assistência circulatória",
            GetTranslation
          )}

        {/* Tempo para Início da Retirada dos Drenos */}
        {renderRangeField(
          formData,
          setFormData,
          "tempo_inicio_retirada_drenos_horas",
          "Tempo para início da retirada dos drenos (horas)",
          0,
          200,
          0,
          GetTranslation
        )}

        <div className="col-12 d-flex justify-content-between">
          <Button label={GetTranslation("Voltar")} className="btn btn-secondary" onClick={handleBack} />
          <Button label={id ? GetTranslation("Update") : GetTranslation("Create")} className="btn btn-primary" type="submit" onClick={confirmSave} />
        </div>
      </form>
    </div>
  );
};

export default PosOperatorioForm;
